/**
 * Stage Status Cell - Displays pipeline stage status indicators
 */

export type StageStatus = '✓' | '✗' | '⚠' | '—';

interface StageStatusCellProps {
  status: StageStatus;
}

const statusColors: Record<StageStatus, string> = {
  '✓': '#2E7D4A', // Success green
  '✗': '#C41E3A', // Error red
  '⚠': '#B8860B', // Warning gold
  '—': '#A0A0A0', // Muted
};

export function StageStatusCell({ status }: StageStatusCellProps) {
  return (
    <td
      className="px-2 py-2 text-center"
      style={{ color: statusColors[status] }}
    >
      {status}
    </td>
  );
}

// Helper function to get stage status from result
export function getStageStatus(result: any, stageName: string): StageStatus {
  switch (stageName) {
    case 'api_call':
      return result.api_call_error ? '✗' : '✓';

    case 'json_parse':
      if (result.api_call_error) return '—';
      return result.json_parse_error ? '✗' : '✓';

    case 'schema_validation':
      if (result.api_call_error || result.json_parse_error) return '—';
      if (!result.schema_validation_error) return '✓';

      const errors = result.schema_validation_error;
      if (errors.length > 0) {
        const hasOnlyMinorErrors = errors.every((error: any) => {
          const isMinimumKeyword = error.keyword === 'minimum';
          const isColsOrRowsPath =
            error.instancePath === '/sheets/0/cols' ||
            error.instancePath === '/sheets/0/rows';
          return isMinimumKeyword && isColsOrRowsPath;
        });
        if (hasOnlyMinorErrors) return '⚠';
      }
      return '✗';

    case 'formula_evaluation':
      if (
        result.api_call_error ||
        result.json_parse_error ||
        result.schema_validation_error
      )
        return '—';
      return result.formula_evaluation_error ? '✗' : '✓';

    default:
      return '—';
  }
}

export function getRepairStatus(result: any): StageStatus {
  if (result.api_call_error || result.json_parse_error) return '—';
  if (result.repair_error === undefined) return '—';
  return result.repair_error ? '✗' : '✓';
}

export function getStatusIndicator(result: any) {
  if (result.error || !result.is_valid_json) {
    return { icon: '✗', color: '#C41E3A', label: 'Failed' };
  } else if (result.formula_errors || !result.evaluation_ok) {
    return { icon: '⚠️', color: '#B8860B', label: 'Warnings' };
  } else {
    return { icon: '✓', color: '#2E7D4A', label: 'Success' };
  }
}
